import { Component } from '@theme/component';

/**
 * The footer's back-to-top button.
 *
 * Replaces the old theme's GSAP `scrollTo` handler with the platform's own smooth scrolling —
 * GSAP is not part of Horizon and the tween was a plain scroll to the top of the document.
 *
 * Two behaviours come from the old theme:
 *   - the button hides while a fullscreen overlay is open, so it cannot sit on top of the
 *     shoppable video popup. The old code toggled a class from inside the popup's own script;
 *     here the button watches `<body>` instead, so no other section has to know about it.
 *   - clicking pushes `back_to_top_click` to the GTM data layer (`leaf_back_to_top.js`).
 *
 * @extends {Component}
 */
class EqFooterBackToTop extends Component {
  /** Classes any fullscreen overlay adds to <body>; the button stays hidden while one is set. */
  static OVERLAY_CLASSES = ['shoppable-video-active', 'hp-shoppable-video-active', 'eq-shoppable-video-popup-open'];

  /** @type {MutationObserver | undefined} */
  #bodyObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#update();

    // `passive` because the handler never calls preventDefault, so the browser can keep
    // scrolling without waiting on it.
    window.addEventListener('scroll', this.#update, { passive: true });

    this.#bodyObserver = new MutationObserver(this.#update);
    this.#bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('scroll', this.#update);
    this.#bodyObserver?.disconnect();
    this.#bodyObserver = undefined;
  }

  get #scrollTop() {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  get #overlayOpen() {
    return EqFooterBackToTop.OVERLAY_CLASSES.some((name) => document.body.classList.contains(name));
  }

  #update = () => {
    const scrolled = this.#scrollTop > window.innerHeight;

    this.toggleAttribute('visible', scrolled && !this.#overlayOpen);
  };

  scrollToTop() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'back_to_top_click' });

    // `scroll-behavior: smooth` on the root would already cover this, but passing it explicitly
    // keeps the button working on pages that reset it.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

if (!customElements.get('eq-footer-back-to-top')) {
  customElements.define('eq-footer-back-to-top', EqFooterBackToTop);
}
